import { Tag } from 'pages/settings/templates/tags';
import { FlexDetailsField } from './event';
import { TemplateService } from './templateService';
import { EntType, ActRateCategory } from '@otm/shared/event';
import type { StaffListItem } from '@otm/shared/contact';

// EntType / ActRateCategory now live in the shared package; re-export for existing importers.
export type { EntType, ActRateCategory };

export type FontFamily =
  | `'Inter', sans-serif`
  | `'Poppins', sans-serif`
  | `'Roboto', sans-serif`
  | `'Public Sans', sans-serif`;
export type PresetColor =
  | 'default'
  | 'theme1'
  | 'theme2'
  | 'theme3'
  | 'theme4'
  | 'theme5'
  | 'theme6'
  | 'theme7'
  | 'theme8';
export type I18n = 'en' | 'fr' | 'ro' | 'zh';

export enum ThemeMode {
  LIGHT = 'light',
  DARK = 'dark'
}

export enum MenuOrientation {
  VERTICAL = 'vertical',
  HORIZONTAL = 'horizontal'
}

export enum ThemeDirection {
  LTR = 'ltr',
  RTL = 'rtl'
}

declare global {
  interface Window {
    FIREBASE_APPCHECK_DEBUG_TOKEN: boolean;
  }
}

export type CustomizationActionProps = {
  type: string;
  payload?: CustomizationProps;
};

export type DefaultConfigProps = {
  /**
   * The props used for the theme font-style.
   * We provide static below options -
   * `'Inter', sans-serif`
   * `'Poppins', sans-serif`
   * `'Roboto', sans-serif`
   * `'Public Sans', sans-serif` (default)
   */
  fontFamily: FontFamily;

  /**
   * The props used for display menu-items with multi-language.
   * We provide static below languages according to 'react-intl' options - https://www.npmjs.com/package/react-intl
   * 'en' (default)
   * 'fr'
   * 'ro'
   * 'zh'
   */
  i18n: I18n;

  /**
   * the props used for menu orientation (diffrent theme layout).
   * we provide static below options -
   * 'vertical' (default) - MenuOrientation.VERTICAL
   * 'horizontal' - MenuOrientation.HORIZONTAL
   */
  menuOrientation: MenuOrientation;

  /**
   * the props used for show mini variant drawer
   * the mini variant is recommended for apps sections that need quick selection access alongside content.
   * default - false
   */
  miniDrawer: boolean;

  /**
   * the props used for theme container.
   * the container centers your content horizontally. It's the most basic layout element.
   * default - true which show container
   * false - will show fluid
   */
  container: boolean;

  /**
   * the props used for default theme palette mode
   * explore the default theme
   * below theme options -
   * 'light' (default) - ThemeMode.LIGHT
   * 'dark' - ThemeMode.DARK
   */
  mode: ThemeMode;

  /**
   * the props used for theme primary color variants
   * we provide static below options thoe s are already defaine in src/themes/theme -
   * 'default'
   * 'theme1'
   * 'theme2'
   * 'theme3'
   * 'theme4'
   * 'theme5'
   * 'theme6'
   * 'theme7'
   * 'theme8'
   */
  presetColor: PresetColor;

  /**
   * the props used for default theme direction
   * explore the default theme
   * below theme options -
   * 'ltr' (default) - ThemeDirection.LTR
   * 'rtl' - ThemeDirection.RTL
   */
  themeDirection: ThemeDirection;
};

export type CustomizationProps = {
  fontFamily: FontFamily;
  i18n: I18n;
  miniDrawer: boolean;
  container: boolean;
  menuOrientation: MenuOrientation;
  mode: ThemeMode;
  presetColor: PresetColor;
  themeDirection: ThemeDirection;
  onChangeContainer: VoidFunction;
  onChangeLocalization: (lang: I18n) => void;
  onChangeMode: (mode: ThemeMode) => void;
  onChangePresetColor: (theme: PresetColor) => void;
  onChangeDirection: (direction: ThemeDirection) => void;
  onChangeMiniDrawer: (miniDrawer: boolean) => void;
  onChangeMenuOrientation: (menuOrientation: MenuOrientation) => void;
  onChangeFontFamily: (fontFamily: FontFamily) => void;
};

export interface Config {
  mainConfig: MainConfig;
  userConfig: UserConfig;
}

export interface ContactTagObject {
  [key: string]: any;
}

export interface PortalTemplateObject {
  id: string;
  name: string;
}

/**
 * Clean-slate segment metadata stored alongside a details template. Mirrors the `segments`
 * collection's structural fields only — instance state (`relEventId`, finalized, modified) is
 * dropped, and `id` is a template-local key (e.g. `seg-0`) re-keyed back to a fresh segment id on
 * load. `isDefault` marks the implicit leading "General" segment so it maps to `${eventId}-default`.
 */
export interface DetailsTemplateSegment {
  id: string;
  isDefault?: boolean;
  title: string;
  includeTime?: boolean;
  onTasks?: boolean;
  rosterAssignable?: boolean;
  order: number;
}

export interface DetailsTemplateObject {
  id: string;
  name: string;
  fields: FlexDetailsField[];
  /** Optional — absent on legacy templates saved before segments were captured. */
  segments?: DetailsTemplateSegment[];
}

export interface TermsTemplateOject {
  id: string;
  name: string;
  html: string;
}

// StaffListItem now lives in the shared package; re-export for existing importers.
export type { StaffListItem };

export interface EmailTemplate {
  id: string;
  name: string;
  html: string;
  subject: string;
  textSupport?: boolean;
  textContent?: string;
}

export interface EmailTemplateBlast extends EmailTemplate {
  lastSent: Date | null;
}

export type ReminderOffsetUnit = 'days' | 'weeks' | 'months';
export type ReminderOffsetDirection = 'before' | 'after';
export type ReminderMode = 'oneOff' | 'recurring';
export type ReminderTargetStatus = 'published' | 'viewed';

export interface ReminderRule {
  id: string;
  label: string;
  enabled: boolean;
  mode: ReminderMode;
  offsetValue: number;
  offsetUnit: ReminderOffsetUnit;
  direction: ReminderOffsetDirection;
  recurringIntervalDays?: number;
  recurringStartOffsetDays?: number;
  recurringMaxSends?: number;
  targetStatuses: ReminderTargetStatus[];
  minAmount?: number;
  subject: string;
  html: string;
}

export interface StaffDigestConfig {
  enabled: boolean;
  leadDays: number;
  includePastDue: boolean;
  includePreApprovedReady: boolean;
  minAmount?: number;
  notificationName: string;
}

export interface PaymentRemindersConfig {
  enabled: boolean;
  dryRun?: boolean;
  rules: ReminderRule[];
  staffDigest: StaffDigestConfig;
  updatedAt?: Date | null;
  updatedBy?: string;
}

export interface UserConfig {
  viewRoute: 'Staff' | 'Talent' | 'Client';
  googleAccess?: {
    accessToken: string;
    refreshToken: string;
    tokenExpirationTime: string;
  } | null;
  notifiedAboutAccounts: boolean;
  expenseTreeGroups?: string[]; // expense categories folded into folders; undefined = all (default on)
}

export interface ActTag {
  id: string;
  name: string;
  color: string;
}

export interface ActTagsConfig {
  tagsArray?: ActTag[];
}

export interface JoinActsCompanies {
  id: string; // Document ID of the join record itself
  actId: string; // Foreign key to EntType (the act)
  companyId: string; // Foreign key to a Company document
  companyName: string; // Added new required field
  actName: string; // Name of the act
}

export interface JoinActsServices {
  id: string; // Document ID of the join record itself
  actId: string; // Foreign key to EntType (the act)
  serviceId: string; // Foreign key to a TemplateService document
  serviceName: string; // Name of the service
  sortOrder?: number; // Display order of the service tab within its act
}

export interface ActCompositePageData {
  act: EntType;
  joins: JoinActsCompanies[]; // An act might be associated with multiple companies
  serviceJoins?: JoinActsServices[]; // Added for services
}

export interface MainConfig {
  app: {
    emailActive: boolean;
  };
  portalFeatures?: {
    stripeSandbox: boolean;
    chaseSandbox?: boolean;
    /** Which system processes ACH payments — cards always go through Stripe */
    achProvider?: 'stripe' | 'chase';
  };
  valueLists: {
    referralSources: string[];
  };
  tagSets: {
    contacts: Tag[];
    contactTagObject?: ContactTagObject;
  };
  defaults: {
    termsDefault: string;
  };
  serviceTemplates: TemplateService[];
  staffList: StaffListItem[];
  portalTemplates: PortalTemplateObject[];
  detailsTemplates: DetailsTemplateObject[];
  termsTemplates: TermsTemplateOject[];
  emailTemplates: EmailTemplate[];
  emailTemplateBlasts: EmailTemplateBlast[];
  paymentReminders?: PaymentRemindersConfig;
  actTags?: ActTagsConfig;
  mailLists: { name: string; id: string }[];
  fieldTemplates: {
    [key: string]: {
      [key: string]: string;
    };
  };
  entTypes: EntType[];
  error?: string | null;
}
